<template>
  <section id="selector" class="selector">
    <div class="selector__wrap">
      <h2>¿Qué vienes a hacer hoy?</h2>
      <p class="selector__lead">Elige un camino y te mostramos solo lo que te interesa.</p>
      <div class="selector__paths">
        <a
          v-for="domain in domains"
          :key="domain.id"
          class="path"
          :data-tone="domain.tone"
          :href="landingSectionHref(domain.anchor)"
          @click="onOpen($event, domain)"
        >
          <span class="path__corner" aria-hidden="true">→</span>
          <h3>{{ domain.exploreTitle }}</h3>
          <p class="path__long">{{ domain.explorationText }}</p>
          <p class="path__short">{{ domain.selectorShort }}</p>
          <div class="path__chips">
            <span v-for="chip in domain.selectorChips" :key="chip" class="path__chip">{{ chip }}</span>
          </div>
          <div v-if="shotFor(domain.id)" class="path__mini">
            <img :src="shotFor(domain.id)?.src" :alt="shotFor(domain.id)?.alt" loading="lazy" />
          </div>
          <p class="path__note">
            <span aria-hidden="true">{{ domain.tone === 'warm' ? '⚡' : '🔒' }}</span>
            {{ domain.selectorNote }}
          </p>
          <span class="path__btn">{{ domain.selectorAction }}</span>
        </a>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import posthog from 'posthog-js'
import { LANDING_DOMAINS } from '@/constants/landingCatalog'
import { landingSectionHref, navigateToLandingSection } from '@/utils/landingHomeNavigation'
import type { LandingDomain } from '@/types/landingCatalog.types'
import shotFichas from '@/assets/landing/shot_fichas.jpg'
import shotComunidad from '@/assets/landing/shot_comunidad_selector.jpg'

interface SelectorShot {
  src: string
  alt: string
}

const SELECTOR_SHOTS: Record<string, SelectorShot> = {
  write: {
    src: shotFichas,
    alt: 'Fichas de personajes, lugares, objetos, especies y criaturas',
  },
  community: {
    src: shotComunidad,
    alt: 'Cápsula del escritor',
  },
}

export default defineComponent({
  name: 'HomeExploreSection',

  setup() {
    return {
      domains: LANDING_DOMAINS,
      landingSectionHref,
    }
  },

  methods: {
    shotFor(id: string) {
      return SELECTOR_SHOTS[id] ?? null
    },

    capture(event: string, properties: Record<string, string>) {
      if (!import.meta.env.VITE_POSTHOG_PROJECT_TOKEN || !import.meta.env.VITE_POSTHOG_HOST) return
      posthog.capture(event, properties)
    },

    async onOpen(event: MouseEvent, domain: LandingDomain) {
      event.preventDefault()
      const heroState = document.getElementById('hero')?.dataset.state
      const properties: Record<string, string> = { path: domain.trackState }
      if (heroState) properties.hero_state = heroState
      this.capture('selector_click', properties)
      await navigateToLandingSection(this.$router, domain.anchor)
    },
  },
})
</script>

<style scoped>
.selector {
  box-sizing: border-box;
  scroll-margin-top: var(--landing-nav-height, 4rem);
  padding: 96px 0 104px;
  background: #10243f;
  color: #fff8ee;
}

.selector__wrap {
  width: min(1200px, 100%);
  margin: 0 auto;
  padding: 0 24px;
}

.selector h2 {
  margin: 0;
  text-align: center;
  font-weight: 800;
  font-size: clamp(34px, 4.4vw, 54px);
  letter-spacing: -0.03em;
}

.selector__lead {
  max-width: 620px;
  margin: 14px auto 44px;
  text-align: center;
  font-size: 18px;
  opacity: 0.85;
}

.selector__paths {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
}

.path {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 34px 34px 30px;
  border-radius: 28px;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease;
}

.path:hover,
.path:focus-visible {
  transform: translateY(-6px);
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.32);
}

.path:focus-visible {
  outline: 3px solid #ff9f1c;
  outline-offset: 4px;
}

.path[data-tone='warm'] {
  color: #1d3557;
  background: linear-gradient(160deg, #fff3e4, #f0a060 42%, #e06a00);
}

.path[data-tone='cool'] {
  color: #f4fbff;
  background: linear-gradient(160deg, #1b8aa3, #145c78 58%, #0d3b66);
}

.path__corner {
  position: absolute;
  top: 26px;
  right: 26px;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 22px;
  font-weight: 700;
  transition: transform 0.22s;
}

.path[data-tone='warm'] .path__corner {
  background: #1d3557;
  color: #fff;
}

.path[data-tone='cool'] .path__corner {
  background: #fff;
  color: #0d3b66;
}

.path:hover .path__corner {
  transform: translateX(4px);
}

.path h3 {
  margin: 0 60px 0 0;
  font-size: 36px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.path__long,
.path__short {
  max-width: 470px;
  margin: 10px 0 18px;
  font-size: 16.5px;
  line-height: 1.5;
}

.path__short {
  display: none;
}

.path__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.path__chip {
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 13.5px;
  font-weight: 700;
}

.path[data-tone='warm'] .path__chip {
  background: rgba(255, 255, 255, 0.75);
  color: #1d3557;
}

.path[data-tone='cool'] .path__chip {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.28);
}

.path__mini {
  margin-bottom: 22px;
  overflow: hidden;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 10px 26px rgba(16, 36, 63, 0.25);
}

.path__mini img {
  display: block;
  width: 100%;
  height: auto;
}

.path__note {
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 600;
}

.path__btn {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  padding: 13px 20px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 16.5px;
}

.path[data-tone='warm'] .path__btn {
  background: #1d3557;
  color: #fff;
}

.path[data-tone='cool'] .path__btn {
  background: #fff;
  color: #0d3b66;
}

@media (max-width: 1020px) {
  .selector__paths {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 640px) {
  .selector {
    display: flex;
    align-items: stretch;
    height: calc(100vh - var(--landing-nav-height, 4rem));
    height: calc(100svh - var(--landing-nav-height, 4rem));
    min-height: 0;
    padding: 0;
    overflow: hidden;
  }

  .selector__wrap {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    padding: clamp(12px, 3svh, 32px) 18px;
  }

  .selector h2 {
    font-size: clamp(26px, 7.4vw, 31px);
    line-height: 1.12;
  }

  .selector__lead {
    margin: 8px auto clamp(14px, 3svh, 28px);
    font-size: 15px;
  }

  .selector__paths {
    gap: clamp(10px, 2svh, 18px);
  }

  .path {
    padding: clamp(16px, 2.6svh, 24px) 18px;
    border-radius: 22px;
  }

  .path:hover {
    transform: none;
  }

  .path h3 {
    margin-right: 48px;
    font-size: clamp(23px, 6.4vw, 27px);
  }

  .path__corner {
    top: clamp(14px, 2.4svh, 20px);
    right: 16px;
    width: 36px;
    height: 36px;
    font-size: 18px;
  }

  .path__long,
  .path__mini,
  .path__note {
    display: none;
  }

  .path__short {
    display: block;
    margin: 6px 0 clamp(10px, 1.8svh, 14px);
    font-size: 15px;
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .path__chips {
    flex-wrap: nowrap;
    overflow-x: auto;
    margin: 0 -18px clamp(12px, 2svh, 18px);
    padding: 0 18px;
    scrollbar-width: none;
    -webkit-mask-image: linear-gradient(90deg, #000 85%, transparent);
    mask-image: linear-gradient(90deg, #000 85%, transparent);
  }

  .path__chips::-webkit-scrollbar {
    display: none;
  }

  .path__chip {
    flex: none;
    padding: 6px 11px;
    font-size: 13px;
  }

  .path__btn {
    align-self: stretch;
    justify-content: center;
    padding: 12px 16px;
    font-size: 16px;
  }
}

@media (max-width: 640px) and (max-height: 700px) {
  .selector__lead {
    display: none;
  }

  .selector h2 {
    margin-bottom: 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .path,
  .path:hover,
  .path__corner,
  .path:hover .path__corner {
    transition: none;
    transform: none;
  }
}
</style>
